import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import { useResponseToApprovingInvoiceReportColumn } from '@/features/approving_invoice_reports/components/List/hooks';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { isCommonError } from '@/libs/typeguard/isError';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import {
  ApprovingInvoiceReportPaymentInfoResponse,
  CommonError,
  CreateApprovingInvoiceReportApprovalRequest,
  useBulkApprovalInvoiceReports,
  useCreateApprovingInvoiceReportApproval,
  useGetApPayeesHook,
  useGetApprovingInvoiceReportApprovalRouteSuspense,
  useGetApprovingInvoiceReportJournalSuspense,
  useGetApprovingInvoiceReportPaymentInfoSuspense,
  useGetDeptsHook,
  useGetOfficeInvoiceReportSettingsSuspense,
  useGetOfficeMembersHook,
  useGetReportSummarySuspense,
  useSearchApprovingInvoiceReports,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';
import { isSuccess } from '../type';
import { handleApprovingInvoiceReportForbiddenStatusCode } from './handleApprovingInvoiceReportForbiddenStatusCode';
import type {
  ApprovingInvoiceReportColumn,
  ExportApprovingInvoiceReportsArgs,
  ExportApprovingInvoiceReportsModernResult,
  FetchApprovingInvoiceReportApprovalRouteHooks,
  FetchApprovingInvoiceReportJournalEntriesHooks,
  FetchApprovingInvoiceReportsArgs,
  FetchApprovingInvoiceReportsModernResult,
  FetchApprovingInvoiceReportSummaryHooks,
  ResponseError,
} from './type';

const ForbiddenStatusCode = ['403', '401'];

export function useApprovingInvoiceReportsSearch(
  args: FetchApprovingInvoiceReportsArgs = {}
): FetchApprovingInvoiceReportsModernResult {
  const { page = 1, perPage = 50, query, order_by } = args;

  const { data, refetch, isFetching } = useSearchApprovingInvoiceReports(
    {
      pagination: {
        per_page: perPage,
        page,
      },
      query: query,
      order_by: order_by
        ? order_by.map((item) => ({
            field: item.field,
            direction: item.direction,
          }))
        : undefined,
    },
    {},
    {
      query: {
        queryKey: [
          'approvingInvoiceReports',
          { page, perPage, query, order_by },
        ],
      },
    }
  );

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data && typeof data.data === 'object' && 'pagination' in data.data
      ? data.data.pagination
      : undefined
  );

  const result = useMemo(
    () =>
      data?.data &&
      typeof data.data === 'object' &&
      'invoice_reports' in data.data
        ? data.data.invoice_reports?.map(
            useResponseToApprovingInvoiceReportColumn<ApprovingInvoiceReportColumn>
          ) ?? []
        : [],
    [data]
  );

  if (!isSuccess(data?.status)) {
    handleApprovingInvoiceReportForbiddenStatusCode(data?.status);
  }

  return {
    pagination: pagination,
    data: result,
    refetch,
    isFetching,
    isError:
      !isSuccess(data?.status) &&
      !ForbiddenStatusCode.includes(data?.status?.toString() || ''),
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    error: { ...((data?.data ?? {}) as CommonError) },
  };
}

export function useExportApprovingInvoiceReports(
  args: ExportApprovingInvoiceReportsArgs = {}
): ExportApprovingInvoiceReportsModernResult {
  const { page = 1, perPage = 25, query, order_by, csv_encode } = args;

  const { data, isFetching, refetch } = useSearchApprovingInvoiceReports(
    {
      pagination: {
        per_page: perPage,
        page,
      },
      query: query,
      order_by: order_by
        ? order_by.map((item) => ({
            field: item.field,
            direction: item.direction,
          }))
        : undefined,
    },
    {
      csv_encode,
    },
    {
      request: {
        headers: {
          Accept: 'text/csv',
        },
        responseType: 'blob',
      },
      query: {
        queryKey: [
          'exportApprovingInvoiceReports',
          { page, perPage, query, order_by, csv_encode },
        ],
        enabled: false,
        staleTime: 0,
        gcTime: 0,
      },
    }
  );

  const result = useMemo(() => {
    if (isSuccess(data?.status) && data?.data instanceof Blob) {
      return data?.data;
    }
    return undefined;
  }, [data]);

  const status = data?.status;

  if (!isSuccess(status)) {
    handleApprovingInvoiceReportForbiddenStatusCode(status);
  }

  return {
    data: result,
    isLoading: isFetching,
    refetch,
    status,
  };
}

export const useApprovingInvoiceReportSummary: FetchApprovingInvoiceReportSummaryHooks =
  (reportId: string) => {
    const { data, error } = useGetReportSummarySuspense(reportId, {
      query: {
        queryKey: ['approvingInvoiceReportSummary', reportId],
      },
    });

    if (error) {
      throw error;
    }

    const body = data?.data;
    const status = data?.status;
    const isSuccessStatus = isSuccess(status);

    return useMemo(() => {
      if (!isSuccessStatus) {
        return {
          error: {
            messages:
              typeof body === 'object' && isCommonError(body)
                ? body.messages
                : undefined,
            code: status?.toString(),
          },
        };
      }
      return {
        data: body,
      };
    }, [body, status, isSuccessStatus]);
  };

export const useApprovingInvoiceReportJournalEntries: FetchApprovingInvoiceReportJournalEntriesHooks =
  (reportId: string) => {
    const { data, error } = useGetApprovingInvoiceReportJournalSuspense(
      reportId,
      {
        query: {
          queryKey: ['approvingInvoiceReportJournalEntries', reportId],
        },
      }
    );

    if (error) {
      throw error;
    }

    const body = data?.data;
    const status = data?.status;
    const isSuccessStatus = isSuccess(status);

    return useMemo(() => {
      if (!isSuccessStatus) {
        return {
          error: {
            messages:
              typeof body === 'object' && isCommonError(body)
                ? body.messages
                : undefined,
            code: status?.toString(),
          },
        };
      }
      return {
        data: body,
      };
    }, [body, status, isSuccessStatus]);
  };

export const useGetOfficeInvoiceReportSettings = () => {
  const { data, error, isLoading } =
    useGetOfficeInvoiceReportSettingsSuspense();
  if (error) throw error;

  if (isSuccess(data?.status)) {
    return {
      data,
      isLoading,
    };
  } else {
    return {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      error: { ...((data?.data ?? {}) as ResponseError) },
      isLoading,
    };
  }
};

export const useGetInvoiceReportPaymentInfo = (
  reportId: string
): {
  data: ApprovingInvoiceReportPaymentInfoResponse | undefined;
  isLoading: boolean;
  error?: ResponseError | undefined;
} => {
  const { data, error, isLoading } =
    useGetApprovingInvoiceReportPaymentInfoSuspense(reportId);
  if (error) throw error;
  if (isSuccess(data?.status)) {
    return {
      data: data?.data,
      isLoading,
    };
  } else {
    return {
      data: undefined,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      error: { ...((data?.data ?? {}) as ResponseError) },
      isLoading,
    };
  }
};
export const useApprovingInvoiceReportApprovalRoute: FetchApprovingInvoiceReportApprovalRouteHooks =
  (reportId: string) => {
    const { data, error } = useGetApprovingInvoiceReportApprovalRouteSuspense(
      reportId,
      {
        query: {
          queryKey: ['approvingInvoiceReportApprovalRoute', reportId],
        },
      }
    );

    if (error) {
      throw error;
    }
    const body = data?.data;

    if (!isSuccess(data?.status)) {
      if (typeof body === 'object' && isCommonError(body)) {
        return {
          error: {
            messages: body?.messages,
            code: data?.status.toString(),
          },
        };
      }
      return {
        error: {
          messages: undefined,
          code: data?.status.toString(),
        },
      };
    }

    return {
      data: body,
    };
  };

// Service for fetching AP Payees data
export const useGetApPayeesData = () => {
  const getApPayeesApi = useGetApPayeesHook();

  const fetchApPayees = async (payeeIds: string[]): Promise<BaseOption[]> => {
    if (payeeIds.length === 0) return [];

    try {
      const response = await getApPayeesApi({
        payee_ids: payeeIds,
        include_inactive: 'true',
        per: payeeIds.length.toString(),
      });

      if (response.data.ap_payees) {
        return response.data.ap_payees.map((payee) => ({
          label: payee.name ?? '',
          value: payee.id ?? '',
          caption: payee.code ?? '',
        }));
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  return {
    fetchApPayees,
  };
};

// Service for fetching Office Members data
export const useGetOfficeMembersData = () => {
  const getOfficeMembersApi = useGetOfficeMembersHook();

  const fetchOfficeMembers = async (
    memberIds: string[]
  ): Promise<BaseOption[]> => {
    if (memberIds.length === 0) return [];
    try {
      const response = await getOfficeMembersApi({
        office_member_ids: memberIds,
      });

      if (response.data.office_members) {
        return response.data.office_members.map((member) => ({
          label: member.name ?? '',
          value: member.id ?? '',
          caption: member.number ?? '',
        }));
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  return {
    fetchOfficeMembers,
  };
};

// Service for fetching Depts data
export const useGetDeptsData = () => {
  const getDeptsApi = useGetDeptsHook();

  const fetchDepts = async (deptIds: string[]): Promise<BaseOption[]> => {
    if (deptIds.length === 0) return [];
    try {
      const response = await getDeptsApi({
        dept_ids: deptIds,
      });

      if (response.data.depts) {
        return response.data.depts.map((dept) => ({
          label: dept.display_name ?? '',
          value: dept.id ?? '',
        }));
      }

      return [];
    } catch (error) {
      return [];
    }
  };
  return {
    fetchDepts,
  };
};

// Mutate service for creating approval
export const useCreateApprovingInvoiceReportApprovalMutation = () => {
  const { mutateAsync: _mutateAsync } =
    useCreateApprovingInvoiceReportApproval();

  const mutateAsync = useCallback(
    async (args: {
      id: string;
      data?: CreateApprovingInvoiceReportApprovalRequest;
    }) => {
      try {
        const resp = await _mutateAsync({ id: args.id, data: args.data ?? {} });
        if (isSuccess(resp.status)) {
          return {
            status: 'success' as const,
            data: resp.data,
          };
        } else {
          return {
            status: 'error' as const,
            errors: {
              code: resp.status.toString(),
              messages:
                typeof resp?.data === 'object' && isCommonError(resp?.data)
                  ? resp?.data?.messages
                  : undefined,
            },
          };
        }
      } catch (error) {
        return {
          status: 'error' as const,
          errors: { messages: undefined },
        };
      }
    },
    [_mutateAsync]
  );

  return { mutateAsync };
};

// Mutate service for bulk approval
export const useCreateBulkApprovingInvoiceReportApprovalMutation = () => {
  const { mutateAsync: _mutateAsync } = useBulkApprovalInvoiceReports();

  const mutateAsync = useCallback(
    async (args: {
      reports: { report_id: string; report_approval_id: string }[];
    }) => {
      try {
        const resp = await _mutateAsync({
          data: {
            reports: args.reports,
          },
        });

        if (isSuccess(resp.status)) {
          return {
            status: 'success' as const,
            data: resp.data,
          };
        }
        return {
          status: 'error' as const,
          errors: {
            code: resp.status.toString(),
            messages:
              typeof resp?.data === 'object' && isCommonError(resp?.data)
                ? resp?.data?.messages
                : undefined,
          },
        };
      } catch (error) {
        return {
          status: 'error' as const,
          errors: { messages: undefined },
        };
      }
    },
    [_mutateAsync]
  );

  return { mutateAsync };
};
